import React, { FC, useState, useEffect } from 'react';
import { Form, Radio, Divider, Header, Icon, Button, Message, Modal } from 'semantic-ui-react';
import { Formik, FormikProps } from 'formik';
import * as Yup from 'yup';

import { BasicDialogContainer } from '@src/views/components/modals/BasicDialogContainer';
import { StyledContainer, Flexbox, FlexItem } from '@src/views/containers';
import { TextField, TextAreaField } from '@src/views/components/forms';
import { ItemWithIcon } from '@src/views/components/display/ItemWithIcon';
import { Size, Align, FlexAlign, FlexJustify, Uuid } from '@src/types';
import { useCurrentUser, useSystemModals, useSystemToasts, useYupExtensions } from '@src/hooks';
import { useCentralCalls } from '@src/rest/CentralCalls';
import { PROJECT_ROLES } from '@src/constants/ProductEnums';
import { FinePrintMessage } from '@src/views/components/util/messaging/FinePrintMessage';
import './ImportDataDialog.scss';

interface ImportDataDialogProps {
    projectId: Uuid;
    projectVersionId: Uuid;
    onComplete?: (result: any) => void;
    onClose?: () => void;
}

enum IMPORT_MODE {
    NEW_VERSION = 'NEW_VERSION',
    EXISTING_VERSION = 'EXISTING_VERSION',
}

interface ImportFormValues {
    importMode: IMPORT_MODE;
    versionName: string;
    versionDescription: string;
}


interface ImportStatRowProps {
    label: string;
    value: string | number;
    danger?: boolean;
}

const ImportStatRow: FC<ImportStatRowProps> = ({ label, value, danger }) => (
    <Flexbox
        justify={FlexJustify.SPACE_BETWEEN}
        align={FlexAlign.CENTER}
        className={`import-stat-row${danger ? ' is-danger' : ''}`}
    >
        <span className="import-stat-label">{label}</span>
        <span className="import-stat-value">{value}</span>
    </Flexbox>
);

const ImportDataDialog: FC<ImportDataDialogProps> = (props) => {
    const { projectId, projectVersionId, onComplete, onClose } = props;

    const { currentUser } = useCurrentUser();
    const modalActions = useSystemModals();

    const isViewer = currentUser?.projects?.find(
        ( p: { projectId: Uuid; projectRole: string } ) => p.projectId === projectId
    )?.projectRole === PROJECT_ROLES.VIEWER;
    const toastActions = useSystemToasts();
    const { importStaticProjectActivities, downloadStaticProjectActivityTemplate } = useCentralCalls();
    const { addStringMinCharDescriptorTest } = useYupExtensions();

    const [ selectedFile, setSelectedFile ]         = useState<File | null>( null ),
          [ isImporting, setIsImporting ]           = useState( false ),
          [ importResults, setImportResults ]       = useState<any>( null ),
          [ importError, setImportError ]           = useState<string | null>( null ),
          [ filePreview, setFilePreview ]           = useState<any[]>( [] ),
          [ isLoadingPreview, setIsLoadingPreview ] = useState( false );

    addStringMinCharDescriptorTest();

    const initialValues: ImportFormValues = {
        importMode: IMPORT_MODE.EXISTING_VERSION,
        versionName: '',
        versionDescription: '',
    };

    const validationSchema = Yup.object().shape({
        importMode: Yup.string().required(),
        versionName: Yup.string().when('importMode', {
            is: IMPORT_MODE.NEW_VERSION,
            then: Yup.string().required('Version name is required'),
            otherwise: Yup.string(),
        }),
        versionDescription: Yup.string(),
    });

    const handleFileSelect = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.xlsx,.xls,.csv';
        input.onchange = () => {
            if (input.files && input.files.length > 0) {
                const file = input.files[0];
                setSelectedFile(file);
                setImportResults(null); // Clear results on new file selection
                setImportError(null);   // Clear errors on new file selection
                setFilePreview([]);     // Clear preview on new file selection

                generateFilePreview(file);
            }
        };
        input.click();
    };
    const excelSerialToDateString = (serial: number) => {
        // Excel stores dates as serial numbers; normalize to YYYY-MM-DD
        const excelEpochOffset = 25569; // Excel 1900 epoch to Unix epoch
        const msPerDay = 86400 * 1000;
        const ms = (serial - excelEpochOffset) * msPerDay;
        const d = new Date(ms);

        const yyyy = d.getUTCFullYear();
        const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
        const dd = String(d.getUTCDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };

    const formatCellForPreview = (value: any): string => {
        if (value == null) return '';

        if (value instanceof Date) {
            const yyyy = value.getFullYear();
            const mm = String(value.getMonth() + 1).padStart(2, '0');
            const dd = String(value.getDate()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd}`;
        }

        if (typeof value === 'number' && value > 25000 && value < 60000) {
            return excelSerialToDateString(value);
        }

        return String(value);
    };
    const generateFilePreview = async (file: File) => {
        setIsLoadingPreview(true);
        try {
            if (file.name.toLowerCase().endsWith('.csv')) {
                const text = await file.text();
                const lines = text.split(/\r?\n/).filter(l => l.length > 0);
                const header = lines[0].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
                const headerLength = header.length;
                const previewData = lines.map((line, i) => {
                    let values = line
                        .split(',')
                        .map(v => v.trim().replace(/^"|"$/g, ''));
                    while (values.length < headerLength) values.push('');
                    return { row: i + 1, data: values };
                });
                setFilePreview(previewData);
            } else {
                const XLSX = await import('xlsx');
                const arrayBuffer = await file.arrayBuffer();

                const wb = XLSX.read(arrayBuffer, { type: 'array', cellDates: true });
                const sheet = wb.SheetNames[0];
                const worksheet = wb.Sheets[sheet];

                const aoa: any[][] = XLSX.utils.sheet_to_json(worksheet, {
                    header: 1,
                    raw: true
                });

                const headerLength = aoa.length > 0 ? (aoa[0]?.length ?? 0) : 0;
                const previewData = aoa.map((row, i) => {
                    const aligned: any[] = new Array(headerLength).fill(undefined);
                    if (row && row.length > 0) {
                        for (let c = 0; c < headerLength; c++) {
                            aligned[c] = c < row.length ? row[c] : undefined;
                        }
                    }
                    return {
                        row: i + 1,
                        data: aligned.map(cellVal => formatCellForPreview(cellVal))
                    };
                });

                setFilePreview(previewData);
            }
        } catch (error) {
            console.error('Error generating preview:', error);
            setFilePreview([]);
        } finally {
            setIsLoadingPreview(false);
        }
    };

    const handleDownloadTemplate = async () => {
        try {
            await downloadStaticProjectActivityTemplate('xlsx', true);
        } catch (err) {
            modalActions.show(
                <StyledContainer padding={Size.SM}>
                    <ItemWithIcon iconName="exclamation triangle" iconColor="red" title="Download failed">
                        {(err as Error).message}
                    </ItemWithIcon>
                </StyledContainer>,
                { size: 'tiny', centered: true }
            );
        }
    };

    const handleImport = async (values: ImportFormValues) => {
        if (!selectedFile) {
            setImportError('Please select a file to import.');
            return;
        }

        setIsImporting(true);
        setImportError(null);
        setImportResults(null);

        try {
            const result = await importStaticProjectActivities(
                selectedFile,
                projectId,
                String(projectVersionId ?? ''),
                currentUser.id,
                values.versionName,
                values.versionDescription
            );

            setImportResults(result);
            console.log("RESULTSSSS");
            console.log(result);
            toastActions.success('Import completed', 'Data has been successfully imported to the project.');

            onComplete?.(result);
        } catch (err) {
            setImportError((err as Error).message || 'An error occurred during import. Please check your file format and try again.');
        } finally {
            setIsImporting(false);
        }
    };

    // Backend may return PascalCase or camelCase
    const relationshipFailures: any[] = importResults
        ? (importResults.RelationshipFailures ?? importResults.relationshipFailures ?? [])
        : [];

    const relationshipFailuresCount = Array.isArray(relationshipFailures) ? relationshipFailures.length : 0;

    const pickField = (obj: any, fieldBase: string) => {
        if (!obj) return '';
        return (
            obj[fieldBase] ??
            obj[fieldBase.charAt(0).toLowerCase() + fieldBase.slice(1)] ??
            obj[fieldBase.toLowerCase()] ??
            ''
        );
    };

    return (
        <Formik<ImportFormValues>
            initialValues={initialValues}
            validateOnChange={true}
            validationSchema={validationSchema}
            onSubmit={handleImport}
        >
            {(formik: FormikProps<ImportFormValues>) => (
                <>
                    <Form onSubmit={formik.handleSubmit}>
                        <BasicDialogContainer
                            title="Import Project Data"
                            roundedTop
                            contentPadding={[Size.MD, Size.MD, Size.LG, Size.MD]}
                            onClose={onClose}
                        >
                          <StyledContainer className="import-data-dialog">
                            <StyledContainer padding={[Size.SM, 0]} marginBottom={Size.MD}>
                                <FinePrintMessage className="import-helper-text" fadeIn={false}>
                                    (Download Template For Upload)
                                </FinePrintMessage>
                                <Button
                                    type="button"
                                    icon="download"
                                    content="Download Template"
                                    basic
                                    color="purple"
                                    onClick={handleDownloadTemplate}
                                    disabled={isViewer}
                                    fluid
                                />
                            </StyledContainer>

                            <Divider />

                            <StyledContainer padding={[Size.SM, 0]} marginBottom={Size.MD}>
                                <Header as="h4" className="import-section-header--tight">
                                    <Icon name="file excel" color="green" />
                                    Select Import File
                                </Header>
                                <Flexbox align={FlexAlign.CENTER} justify={FlexJustify.SPACE_BETWEEN}>
                                    <FlexItem grow>
                                        <StyledContainer
                                            padding={Size.SM}
                                            className="import-file-dropzone"
                                        >
                                            {selectedFile ? (
                                                <ItemWithIcon iconName="file" iconColor="blue" title={selectedFile.name}>
                                                    {(selectedFile.size / 1024).toFixed(2)} KB
                                                </ItemWithIcon>
                                            ) : (
                                                <em className="import-file-empty">No file selected</em>
                                            )}
                                        </StyledContainer>
                                    </FlexItem>
                                    <FlexItem>
                                        <Button
                                            type="button"
                                            icon="upload"
                                            content="Choose File"
                                            color="purple"
                                            onClick={handleFileSelect}
                                            disabled={isViewer}
                                            className="import-choose-file-btn"
                                        />
                                    </FlexItem>
                                </Flexbox>
                            </StyledContainer>

                            <Divider />

                            <StyledContainer padding={[Size.SM, 0]} marginBottom={Size.MD}>
                                <Header as="h4" className="import-section-header">
                                    <Icon name="settings" color="purple" />
                                    Import Options
                                </Header>

                                <Form.Field>
                                    <Radio
                                        label="Create New Project Version"
                                        name="importMode"
                                        value={IMPORT_MODE.NEW_VERSION}
                                        checked={formik.values.importMode === IMPORT_MODE.NEW_VERSION}
                                        onChange={() => formik.setFieldValue('importMode', IMPORT_MODE.NEW_VERSION)}
                                        disabled={isViewer}
                                    />
                                    <StyledContainer className="import-option-hint">
                                        Import data into a new version of the current project
                                    </StyledContainer>
                                </Form.Field>

                                <Form.Field className="import-mode-field">
                                    <Radio
                                        label="Add to Existing Version"
                                        name="importMode"
                                        value={IMPORT_MODE.EXISTING_VERSION}
                                        checked={formik.values.importMode === IMPORT_MODE.EXISTING_VERSION}
                                        onChange={() => formik.setFieldValue('importMode', IMPORT_MODE.EXISTING_VERSION)}
                                        disabled={isViewer}
                                    />
                                    <StyledContainer className="import-option-hint">
                                        Add imported data to the current project version
                                    </StyledContainer>
                                </Form.Field>
                            </StyledContainer>

                        <Divider />

                            {formik.values.importMode === IMPORT_MODE.NEW_VERSION ? (
                                <StyledContainer padding={[Size.SM, 0]} marginBottom={Size.MD}>
                                    <Header as="h4" className="import-section-header">
                                        <Icon name="plus square" color="purple" />
                                        New Version Details
                                    </Header>
                                    <TextField
                                        name="versionName"
                                        label="Version Name"
                                        placeholder="e.g., Imported Version - Q4 2025"
                                        required
                                    />
                                    <TextAreaField
                                        name="versionDescription"
                                        label="Description"
                                        placeholder="Describe this imported version..."
                                    />
                                </StyledContainer>
                            ) : (
                                <StyledContainer padding={[Size.SM, 0]} marginBottom={Size.MD}>
                                    <Message info>
                                        <Message.Header>
                                            <Icon name="info circle" />
                                            Adding to Existing Version
                                        </Message.Header>
                                        <p>
                                            Data will be imported and added to the current project version. Existing data will not be modified.
                                        </p>
                                    </Message>
                                </StyledContainer>
                            )}

                            <Divider />
                            {!importResults ? (
                                <StyledContainer padding={[Size.SM, 0]} marginBottom={Size.MD}>
                                    <Header as="h4" className="import-section-header">
                                        <Icon name="eye" color="blue" />
                                        Data Preview
                                    </Header>

                                    {isLoadingPreview ? (
                                        <Message info>
                                            <Message.Header>
                                                <Icon name="spinner" loading />
                                                Loading Preview...
                                            </Message.Header>
                                        </Message>
                                    ) : filePreview.length > 0 ? (
                                        <StyledContainer className="import-panel">
                                            <Flexbox align={FlexAlign.CENTER} justify={FlexJustify.START} className="import-panel-header">
                                                <Icon name="table" color="blue" />
                                                <span className="import-panel-header-title">File Preview</span>
                                                <span className="import-panel-header-meta">
                                                    {Math.max(0, filePreview.length - 1)}
                                                </span>
                                            </Flexbox>

                                            <StyledContainer className="import-preview-scroll">
                                                {filePreview.map((row, index) => (
                                                    <Flexbox
                                                        key={index}
                                                        justify={FlexJustify.START}
                                                        className={`import-preview-row${index === 0 ? ' is-header' : ''}`}
                                                    >
                                                        <StyledContainer className="import-preview-row-num">
                                                            {row.row}
                                                        </StyledContainer>
                                                        {row.data.map((cell: string, cellIndex: number) => (
                                                            <StyledContainer
                                                                key={cellIndex}
                                                                className="import-preview-cell"
                                                                title={cell}
                                                            >
                                                                {cell}
                                                            </StyledContainer>
                                                        ))}
                                                    </Flexbox>
                                                ))}
                                            </StyledContainer>

                                            <StyledContainer className="import-panel-footer">
                                                Showing {filePreview.length} rows
                                            </StyledContainer>
                                        </StyledContainer>
                                    ) : selectedFile ? (
                                        <Message warning>
                                            <Message.Header>
                                                <Icon name="exclamation triangle" />
                                                Unable to Preview
                                            </Message.Header>
                                            <p>
                                                Could not generate preview for this file. The import will still work -
                                                data will be imported and added to the current project version.
                                            </p>
                                        </Message>
                                    ) : (
                                        <Message info>
                                            <Message.Header>
                                                <Icon name="info circle" />
                                                Ready to Import
                                            </Message.Header>
                                            <p>
                                                Select a file to see a preview of the data that will be imported.
                                                Data will be added to the current project version without modifying existing data.
                                            </p>
                                        </Message>
                                    )}
                                </StyledContainer>
                            ) : (
                                <StyledContainer padding={[Size.SM, 0]} marginBottom={Size.MD}>

                                    <StyledContainer className="import-panel">
                                        <Flexbox align={FlexAlign.CENTER} justify={FlexJustify.START} className="import-panel-header">
                                            <Icon name="check circle" color="green" />
                                            Import Completed Successfully
                                        </Flexbox>

                                        <StyledContainer className="import-panel-body">
                                            <StyledContainer className="import-stat-list">
                                                <ImportStatRow label="Created Categories:" value={importResults.CreatedCategories || 0} />
                                                <ImportStatRow label="Created SubCategories:" value={importResults.CreatedSubCategories || 0} />
                                                <ImportStatRow label="Created SubObjectives:" value={importResults.CreatedSubObjectives || 0} />
                                                <ImportStatRow label="Created Activities:" value={importResults.CreatedActivities || 0} />
                                                <ImportStatRow label="Created Static Project Activities:" value={importResults.CreatedStaticProjectActivities || 0} />
                                                <ImportStatRow label="Failed Relationship Imports:" value={relationshipFailuresCount} danger />

                                                {relationshipFailuresCount > 0 ? (
                                                    <StyledContainer className="import-failure-list">
                                                        {relationshipFailures.map((f, i) => (
                                                            <StyledContainer key={i} className="import-failure-item">
                                                                <StyledContainer className="import-failure-title">{pickField(f, 'ChildActivityName') || pickField(f, 'childActivityName') || `Child #${i+1}`}</StyledContainer>
                                                                <StyledContainer className="import-failure-detail">
                                                                    <div><strong>ParentActivityName:</strong> {pickField(f, 'ParentActivityName')}</div>
                                                                    <div><strong>Reason:</strong> {pickField(f, 'Reason')}</div>
                                                                    <div><strong>MatchesFound:</strong> {pickField(f, 'MatchesFound') ?? ''}</div>
                                                                </StyledContainer>
                                                            </StyledContainer>
                                                        ))}
                                                    </StyledContainer>
                                                ) : (
                                                    <StyledContainer className="import-failure-empty">
                                                        <em>0 failed relationship imports</em>
                                                    </StyledContainer>
                                                )}
                                            </StyledContainer>
                                        </StyledContainer>

                                        <StyledContainer className="import-panel-footer">
                                            Import completed successfully
                                        </StyledContainer>
                                    </StyledContainer>
                                </StyledContainer>
                            )}


                            {importError && (
                                <>
                                    <Divider />
                                    <StyledContainer padding={[Size.SM, 0]}>
                                        <Message error>
                                            <Message.Header>
                                                <Icon name="exclamation triangle" />
                                                Import Failed
                                            </Message.Header>
                                            <Message.Content>{importError}</Message.Content>
                                        </Message>
                                    </StyledContainer>
                                </>
                            )}

                            {!importResults && (
                                <>
                                    <Divider />
                                    { isViewer ? (
                                        <Message info>
                                            <Icon name="lock" />
                                            You have view-only access to this project. Contact an administrator to import data.
                                        </Message>
                                    ) : (
                                        <StyledContainer textAlign={Align.RIGHT}>
                                            <Button
                                                type="submit"
                                                color="purple"
                                                content="Import Data"
                                                loading={isImporting}
                                                disabled={!formik.isValid || !selectedFile || isImporting}
                                            />
                                        </StyledContainer>
                                    ) }
                                </>
                            )}

                          </StyledContainer>
                        </BasicDialogContainer>
                    </Form>

                    <Modal.Actions>
                        <StyledContainer textAlign={Align.RIGHT}>
                            <Button
                                type="button"
                                basic
                                content="Cancel"
                                onClick={onClose}
                            />
                            <Button
                                type="button"
                                primary
                                content="Ok"
                                onClick={onClose}
                            />
                        </StyledContainer>
                    </Modal.Actions>
                </>
            )}
        </Formik>
    );
};

export { ImportDataDialog };